import { useEffect, useRef, useState } from "react";
import { presetRange, PRESETS } from "@/components/DateRange";

/**
 * Unduh Laporan PDF — tombol GLOBAL (dipasang di Topbar, muncul di semua menu tenant).
 * Self-contained: pengguna pilih jenis laporan + rentang, lalu POST /api/v1/reports
 * (PDF) dan unduh sebagai berkas. Rentang default 7 hari (selaras DateRange).
 *
 * Angka di PDF DETERMINISTIK dari basis data canonical (report engine P3) — sumber
 * yang sama dengan dashboard/analytics, jadi laporan tidak akan berbeda dari layar.
 */
type Jenis = "EXECUTIVE_BRIEF" | "FULL_REPORT";

export function ReportButton() {
  const [buka, setBuka] = useState(false);
  const [jenis, setJenis] = useState<Jenis>("FULL_REPORT");
  const [preset, setPreset] = useState<string>("7d");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!buka) return;
    const diLuar = (e: PointerEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setBuka(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setBuka(false); };
    document.addEventListener("pointerdown", diLuar);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("pointerdown", diLuar); document.removeEventListener("keydown", esc); };
  }, [buka]);

  async function unduh() {
    setBusy(true); setErr(null);
    try {
      // yesterday = brief 1 hari; selain itu pakai rentang preset.
      const { start, end } = preset === "yesterday" ? presetRange("yesterday") : presetRange(preset);
      const r = await fetch("/api/v1/reports", {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ type: jenis, start, end, withNarrative: true }),
      });
      if (!r.ok) throw new Error(`Gagal membuat laporan (${r.status})`);
      const blob = await r.blob();
      // Nama berkas dari header bila ada, jika tidak susun sendiri.
      const cd = r.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m?.[1] || `AVIO_${jenis === "EXECUTIVE_BRIEF" ? "Brief" : "Laporan"}_${start}_${end}.pdf`;
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(href);
      setBuka(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Gagal membuat laporan");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div ref={ref} style={{ position: "relative", flex: "none" }}>
      <button type="button" className="avio-btn" data-variant="secondary" data-size="sm"
        onClick={() => setBuka((v) => !v)} aria-haspopup="menu" aria-expanded={buka}
        title="Unduh laporan PDF">
        <i className="fa-solid fa-file-pdf" /> Laporan
      </button>

      {buka && (
        <div role="menu" style={{
          position: "absolute", top: "calc(100% + 8px)", right: 0, minWidth: 280, zIndex: 60,
          background: "var(--color-surface)", border: "1px solid var(--color-border)",
          borderRadius: "var(--radius-md)", boxShadow: "0 18px 42px -18px rgba(4, 12, 32, .45)",
          padding: "12px 14px", textAlign: "left",
        }}>
          <div className="avio-t-sm" style={{ fontWeight: "var(--weight-semi)" as any, marginBottom: 8 }}>Unduh Laporan PDF</div>

          <div className="avio-t-xs avio-muted" style={{ marginBottom: 4 }}>Jenis</div>
          <div className="avio-segment" style={{ marginBottom: 10 }}>
            <button className="avio-segment__item" aria-pressed={jenis === "FULL_REPORT"} onClick={() => setJenis("FULL_REPORT")}>Lengkap</button>
            <button className="avio-segment__item" aria-pressed={jenis === "EXECUTIVE_BRIEF"} onClick={() => setJenis("EXECUTIVE_BRIEF")}>Ringkas</button>
          </div>

          <div className="avio-t-xs avio-muted" style={{ marginBottom: 4 }}>Rentang</div>
          <div className="avio-segment" style={{ marginBottom: 12, flexWrap: "wrap" }}>
            {PRESETS.filter(([v]) => v !== "custom").map(([val, label]) => (
              <button key={val} className="avio-segment__item" aria-pressed={preset === val} onClick={() => setPreset(val)}>{label}</button>
            ))}
          </div>

          {err && <div className="avio-t-xs" style={{ color: "var(--status-danger)", marginBottom: 8 }}>{err}</div>}

          <button type="button" className="avio-btn" data-variant="primary" data-size="sm"
            style={{ width: "100%", justifyContent: "center" }} onClick={unduh} disabled={busy}>
            <i className={`fa-solid ${busy ? "fa-circle-notch fa-spin" : "fa-download"}`} /> {busy ? "Menyiapkan…" : "Unduh PDF"}
          </button>
        </div>
      )}
    </div>
  );
}
